import { useState } from 'react';
import { IconButton } from '@mui/material';
import { Delete } from '@mui/icons-material';
import Box from '@mui/material/Box';
import { useDelete, useNotify } from 'react-admin';
import {
    AVATAR_RESOURCE_SUPPORTED,
    AVATARS,
    DISORDER_TREATMENT_EXPORT_TAG,
} from '@/utils/CONST';
import DeleteDialog from '@/components/common/delete-dialog/delete-dialog';
import { Avatar } from '@/utils/types';
import { useParams } from 'react-router';
import extractIdFromURI from '@/utils/id';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import { ExportDisorderTreatmentDialog } from '@/components/disorders-treatment/export-disorder-treatment/export-disorder-treatment';
import useGetLastMedia from '@/hooks/useGetLastMedia';

interface Props {
    avatar: Avatar;
    withDelete?: boolean;
}

const DisorderTreatmentActionField = ({ avatar, withDelete = false }: Props) => {
    const [deleteOne, { isLoading }] = useDelete();
    const notify = useNotify();
    const { placeId } = useParams();

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

    const itemId = extractIdFromURI(avatar.id) ?? '';

    const { mediaData: existingPdf } = useGetLastMedia({
        resourceId: itemId,
        resourceSupported: AVATAR_RESOURCE_SUPPORTED,
        tagsNames: [DISORDER_TREATMENT_EXPORT_TAG],
    });

    const openExportDialog = () => {
        setIsExportDialogOpen(true);
    };

    const handleDeleteOne = () => {
        deleteOne(
            AVATARS,
            {
                id: avatar,
                meta: {
                    requestType: 'update',
                    id: `${avatar.id}/enable`,
                    data: {
                        enabled: false,
                        children: false,
                    },
                },
            },
            {
                onSuccess: () => {
                    notify('ra.notification.deleted', {
                        type: 'success',
                        messageArgs: { smart_count: 1 },
                    });
                    setOpenDeleteModal(false);
                },
                mutationMode: 'optimistic',
            },
        );
    };

    const handleDownload = () => {
        if (existingPdf?.downloadUrl) {
            window.open(existingPdf.downloadUrl, '_blank');
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'end',
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                }}
            >
                {existingPdf && (
                    <IconButton
                        color={'primary'}
                        sx={{
                            p: 0.5,
                        }}
                        onClick={handleDownload}
                    >
                        <DownloadIcon />
                    </IconButton>
                )}

                <IconButton
                    color={'primary'}
                    sx={{
                        p: 0.5,
                    }}
                    onClick={openExportDialog}
                    disabled={!placeId}
                >
                    <PictureAsPdfIcon />
                </IconButton>

                {placeId && (
                    <ExportDisorderTreatmentDialog
                        open={isExportDialogOpen}
                        onClose={() => setIsExportDialogOpen(false)}
                        placeId={placeId}
                        itemId={itemId}
                    />
                )}
            </Box>

            {withDelete && (
                <IconButton
                    disabled={isLoading}
                    onClick={() => setOpenDeleteModal(true)}
                    color={'primary'}
                    sx={{
                        p: 0.5,
                    }}
                >
                    <Delete />
                </IconButton>
            )}

            {withDelete && openDeleteModal && (
                <DeleteDialog
                    isLoading={isLoading}
                    onClose={() => setOpenDeleteModal(false)}
                    onClick={handleDeleteOne}
                />
            )}
        </Box>
    );
};

export default DisorderTreatmentActionField;

